import { flowrateSchema } from '@enmon/commons/lib/shared/meter/flowrate';
import {
  isBooleanDetailKey,
  isCurrentDetailKey,
  isNumericDetailKey,
  isSubmitOnlyDetailKey,
} from '@/features/sync/engine/inventorization-submit-detail-keys';

const WIDTH_UNIT_DIN = 'DIN';

export function normalizeSubmitDetail(detail: Record<string, string>) {
  const normalized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(detail)) {
    if (isSubmitOnlyDetailKey(key)) {
      continue;
    }

    if (key === 'flowrate') {
      normalized[key] = toFlowrate(value);
      continue;
    }

    if (isBooleanDetailKey(key)) {
      normalized[key] = toBoolean(value);
      continue;
    }

    if (isCurrentDetailKey(key)) {
      normalized[key] = toCurrentValue(value);
      continue;
    }

    if (isNumericDetailKey(key)) {
      normalized[key] = toSubmitFiniteNumber(value);
      continue;
    }

    normalized[key] = nullIfEmpty(value);
  }

  return {
    ...normalized,
    flowrate: toFlowrateFromDetail(detail),
    width: toWidthInMillimeters(detail),
  };
}

export function toSubmitFiniteNumber(value: unknown) {
  const parsed = Number(typeof value === 'string' ? value.replace(',', '.') : value);

  return Number.isFinite(parsed) ? parsed : null;
}

function toFlowrateFromDetail(detail: Record<string, string>) {
  const unit = nullIfEmpty(detail['flowrate.unit']);
  const value = nullIfEmpty(detail['flowrate.value']);

  if (unit && value) {
    return toFlowrate(`${unit} ${value}`);
  }

  return toFlowrate(detail.flowrate);
}

function toFlowrate(value: unknown) {
  const text = nullIfEmpty(value);

  if (!text) {
    return null;
  }

  const parsed = flowrateSchema.safeParse(text);

  return parsed.success ? parsed.data : null;
}

function toWidthInMillimeters(detail: Record<string, string>) {
  const rawValue = detail['width.value'] ?? detail.width;
  const value = toSubmitFiniteNumber(rawValue);

  if (value === null) {
    return null;
  }

  return detail['width.unit'] === WIDTH_UNIT_DIN ? value * 17.5 : value;
}

function toCurrentValue(value: unknown) {
  const number = toSubmitFiniteNumber(value);

  if (number !== null) {
    return number;
  }

  const currentMatch = String(value ?? '').match(/Current-(\d+(?:\.\d+)?)$/);

  return currentMatch ? toSubmitFiniteNumber(currentMatch[1]) : null;
}

function toBoolean(value: unknown) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
  }

  return null;
}

function nullIfEmpty(value: unknown) {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';

  return text || null;
}
